import {
  useCopySkillTemplate,
  useUnpublishSkillTemplate,
} from "@/api/skill-templates";
import { useUserInfo } from "@/api/user";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAppNavigate } from "@/hooks/pane-routing";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import { RailRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRow";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  useObjectActions,
  useSkillTemplateObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import { useOpenSkillTemplate } from "@/panes/skill-template/useOpenSkillTemplate";
import { useOpenSkill } from "@/panes/skill/useOpenSkill";
import { type SkillTemplate } from "@cohesive/api-client";
import { Icon } from "@cohesive/ui-components";
import { Check, ScrollText } from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";

const SKILL_TEMPLATE_KIND: ObjectKind = { type: "skill-template" };

// Matches the AlertDialog exit animation so the pane (when open on this template)
// unmounts only after Radix restores <body> pointer-events.
const ALERT_DIALOG_EXIT_MS = 200;

export function TemplatesRailRow({
  template,
  active,
}: {
  template: SkillTemplate;
  active?: boolean;
}) {
  const openTemplate = useOpenSkillTemplate();
  const openSkill = useOpenSkill();
  const appNavigate = useAppNavigate();
  const copyTemplate = useCopySkillTemplate();
  const isOrgAdmin = useIsOrgAdmin();
  const { data: me } = useUserInfo();
  const menu = useRailRowContextMenu();
  const [unpublishOpen, setUnpublishOpen] = useState(false);

  // Only org templates can be unpublished, and only by the publisher or an org admin.
  const canUnpublish =
    template.type === "organization" &&
    (isOrgAdmin || template.published_by_user_id === me?.user.id);

  const { mutate: unpublishTemplate, isPending: isUnpublishing } =
    useUnpublishSkillTemplate({
      onSuccess: () => {
        toast.success("Template unpublished");
        setUnpublishOpen(false);
        // If the pane is open on this template, close it once the row is gone.
        if (active) {
          window.setTimeout(
            () => appNavigate({ pane: "rightPane", to: null }),
            ALERT_DIALOG_EXIT_MS,
          );
        }
      },
      onError: (error) =>
        toast.error(error.message || "Failed to unpublish template"),
    });

  const handleAddToMySkills = () => {
    // Guard against duplicate copies while one is already in flight (the menu
    // item is no longer visually disabled after the registry migration).
    if (copyTemplate.isPending) return;
    copyTemplate.mutate(template.id, {
      onSuccess: (skill) => {
        toast.success("Added to your skills");
        openSkill(skill.id);
      },
      onError: () => toast.error("Failed to copy template."),
    });
  };

  const templateActionDeps = useMemo(
    () => ({
      addToMySkills: handleAddToMySkills,
      unpublish: canUnpublish ? () => setUnpublishOpen(true) : undefined,
    }),
    // handleAddToMySkills is recreated each render but only closes over stable
    // refs (copyTemplate/openSkill); gate solely on canUnpublish.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canUnpublish],
  );

  const handlers = useSkillTemplateObjectActionHandlers(templateActionDeps);
  const { actions } = useObjectActions({
    kind: SKILL_TEMPLATE_KIND,
    surface: "left-rail",
    enabled: menu.open,
    state: { canUnpublish },
    handlers,
  });

  return (
    <>
      <div className="group/row relative" onContextMenu={menu.onContextMenu}>
        <RailRow
          icon={ScrollText}
          label={template.name}
          active={active}
          className="pr-8"
          leading={
            template.icon?.type ? (
              <span className="shrink-0 text-n-50">
                <Icon icon={template.icon} size={16} />
              </span>
            ) : undefined
          }
          trailing={
            template.already_copied ? (
              <Check
                className="ml-1 size-3 shrink-0 text-lc-active"
                aria-label="Added"
              />
            ) : undefined
          }
          onClick={() => openTemplate(template.id)}
        />

        {/* Registry-driven (surface `left-rail`). RailRowMenu stays `modal={false}`
            under the hood: the Unpublish action opens an AlertDialog — a modal
            dropdown + dialog both lock <body> pointer-events and Radix miscounts
            them, freezing the app. */}
        <ObjectActionMenu
          actions={actions}
          kind={SKILL_TEMPLATE_KIND}
          name={template.name}
          menuController={menu}
          label={template.name}
        />
      </div>

      <AlertDialog open={unpublishOpen} onOpenChange={setUnpublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish this template?</AlertDialogTitle>
            <AlertDialogDescription>
              Members will no longer be able to copy &quot;{template.name}
              &quot;. Skills people already copied are untouched. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnpublishing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isUnpublishing}
              onClick={(e) => {
                e.preventDefault();
                unpublishTemplate(template.id);
              }}
            >
              Unpublish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
